import React, {memo} from "react";
import Picture from "./common/Picture";
import StoryCollection from "../utils/StoryCollection";
import {ArrowRightIcon} from "./common/Icons";
import {useStoryCarousel} from "../hooks/useStoryCarousel";

interface TwoPaneSectionProps {
    id: string;
    title: string;
    main?: string;
    stories: StoryCollection;
}

export const TwoPaneSection: React.FC<TwoPaneSectionProps> = memo(({id, title, main, stories}) => {
    const {
        storyKeys,
        activeStoryKey,
        activeSlideIndex,
        activeStorySlides,
        handleNextSlide,
        handlePrevSlide,
        DURATION,
    } = useStoryCarousel(stories);

    return (
        <section
            id={id}
            className="snap-section pt-4 sm:pt-6 lg:pt-8 bg-dark-mode text-ivory flex flex-col"
        >
            <div className="flex-1 flex flex-col px-4 sm:px-6 lg:px-0">
                {/* Title */}
                <h2 className="heading-font text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center text-ivory mb-4 sm:mb-6 lg:mb-8 leading-tight">
                    {title}
                </h2>

                {/* Main Content */}
                <div className="flex-grow min-h-0 flex flex-col md:flex-row">
                    {/* Left Pane (Static) */}
                    {main && (
                        <div className="relative hidden lg:block lg:flex-1 overflow-hidden">
                            <Picture
                                {...(main.toLowerCase().endsWith('.webp') ? { webpSrc: main } : main.toLowerCase().endsWith('.png') ? { pngSrc: main } : { jpgSrc: main })}
                                alt="Ace Club Card"
                                className="absolute inset-0 w-full h-full object-cover"
                                onErrorSrc={'https://placehold.co/600x800/153D40/C6A375?text=Club+Card'}
                            />
                        </div>
                    )}

                    {/* Right Pane (Slider) */}
                    <div className="relative flex-1 overflow-hidden rounded-lg lg:rounded-none">
                        {/* Story Image Container */}
                        <div className="relative w-full h-64 sm:h-80 md:h-full overflow-hidden bg-muted-sage">
                            <Picture
                                {...(activeStorySlides[activeSlideIndex].toLowerCase().endsWith('.webp') ? { webpSrc: activeStorySlides[activeSlideIndex] } : activeStorySlides[activeSlideIndex].toLowerCase().endsWith('.png') ? { pngSrc: activeStorySlides[activeSlideIndex] } : { jpgSrc: activeStorySlides[activeSlideIndex] })}
                                alt={`${activeStoryKey} slide ${activeSlideIndex + 1}`}
                                className="absolute inset-0 w-full h-full object-cover transition-opacity ken-burns duration-500"
                                onErrorSrc={'https://placehold.co/600x400/708978/F6F4E9?text=Story+Image'}
                            />

                            {/* Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-40"/>

                            {/* Progress Indicators */}
                            <div className="absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 z-20">
                                <div className="flex space-x-1 sm:space-x-2">
                                    {activeStorySlides.map((_, index) => (
                                        <div
                                            key={index}
                                            className="flex-1 h-0.5 sm:h-1 bg-white bg-opacity-30 rounded-full overflow-hidden"
                                        >
                                            <div
                                                key={`${activeStoryKey}-${index}`}
                                                className={`h-full bg-white transition-all duration-200
                                                ${index === activeSlideIndex ? 'story-progress' : ''}
                                                ${index < activeSlideIndex ? 'w-full' : 'w-0'}`}
                                                style={{animationDuration: `${DURATION}ms`}}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Interaction Area */}
                            <div className="absolute inset-0 flex z-10">
                                <button
                                    className="w-1/2 h-full cursor-pointer focus:outline-none active:bg-royal-pine active:bg-opacity-10 transition-colors tap-highlight-none"
                                    onClick={handlePrevSlide}
                                    aria-label="Previous slide"
                                />
                                <button
                                    className="w-1/2 h-full cursor-pointer focus:outline-none active:bg-royal-pine active:bg-opacity-10 transition-colors tap-highlight-none"
                                    onClick={handleNextSlide}
                                    aria-label="Next slide"
                                />
                            </div>
                        </div>

                        {/* Content Footer */}
                        <div
                            className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-royal-pine text-ivory">
                            <div className="flex justify-between items-start sm:items-center">
                                {/* Slide Title */}
                                <div className="flex-1 pr-4">
                                    <h3 className="heading-font text-lg sm:text-xl lg:text-2xl mb-1 sm:mb-0">
                                        {storyKeys[storyKeys.indexOf(activeStoryKey)]}
                                    </h3>
                                </div>
                                {/* Next Button */}
                                <button
                                    onClick={handleNextSlide}
                                    className="flex items-center space-x-2 border-b border-ivory pb-1 hover:border-logo-color hover:text-logo-color transition-colors focus:outline-none text-sm sm:text-base"
                                    aria-label="Next slide"
                                >
                                    <span className="font-medium">NEXT</span>
                                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5"/>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});

TwoPaneSection.displayName = 'TwoPaneSection';